import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Search, Eye, Edit, Copy, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Company {
  id: string;
  razaoSocial: string;
  nomeFantasia: string;
  cnpj: string;
  segmento: string;
  beneficiados: number;
  status: "Ativo" | "Inativo" | "Pendente";
}

const CategoryItems: React.FC = () => {
  const { categoryId, subcategoryId } = useParams<{ categoryId: string; subcategoryId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSegment, setSelectedSegment] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    razaoSocial: "",
    cnpj: "",
    nomeFantasia: "",
    responsavelTecnico: "",
    emailTecnico: "",
    webhookUrl: ""
  });

  // Gerar credenciais de API
  const generateClientId = () => {
    const prefix = subcategoryId === "aereo" ? "av" : subcategoryId === "terrestre" ? "tr" : "co";
    const random = Math.random().toString(36).substring(2, 11);
    return `${prefix}_${random}`;
  };

  const generateClientSecret = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const [apiCredentials, setApiCredentials] = useState({
    clientId: generateClientId(),
    clientSecret: generateClientSecret()
  });

  // Máscara CNPJ
  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 14) {
      return numbers
        .replace(/(\d{2})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1/$2")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }
    return value;
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJ(e.target.value);
    setFormData({ ...formData, cnpj: formatted });
  };

  // Copiar para clipboard
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copiado!",
      description: `${label} copiado para a área de transferência.`,
    });
  };

  // Salvar empresa
  const handleSave = () => {
    // Validação básica
    if (!formData.razaoSocial || !formData.cnpj || !formData.nomeFantasia || !formData.responsavelTecnico || !formData.emailTecnico) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    // Em produção, aqui faria uma chamada à API
    console.log("Dados da empresa:", formData);
    console.log("Credenciais de API:", apiCredentials);

    toast({
      title: "Empresa cadastrada!",
      description: "As credenciais de API foram geradas e o manual de integração será enviado por e-mail.",
    });

    // Fechar modal e resetar form
    setIsModalOpen(false);
    setFormData({
      razaoSocial: "",
      cnpj: "",
      nomeFantasia: "",
      responsavelTecnico: "",
      emailTecnico: "",
      webhookUrl: ""
    });
    setApiCredentials({
      clientId: generateClientId(),
      clientSecret: generateClientSecret()
    });
  };

  // Mock data - em produção viria de uma API
  // TODO: Este valor deve ser um count() dinâmico da tabela de usuários vinculados à empresa.
  const allCompanies: Company[] = [
    {
      id: "1",
      razaoSocial: "Aviões S.A.",
      nomeFantasia: "Aviões",
      cnpj: "06.164.253/0001-87",
      segmento: "Transporte Aéreo",
      beneficiados: 1, // TODO: Este valor deve ser um count() dinâmico da tabela de usuários vinculados à empresa. Atualmente: João Silva.
      status: "Ativo"
    },
    {
      id: "hotel-mar-brasil",
      razaoSocial: "Hotel Mar do Brasil Ltda",
      nomeFantasia: "Hotel Mar",
      cnpj: "12.345.678/0001-90",
      segmento: "Hotelaria",
      beneficiados: 2150,
      status: "Ativo"
    }
  ];

  // Filtrar empresas baseado na categoria/subcategoria
  const companies = allCompanies.filter(company => {
    if (categoryId === "hotel") {
      return company.segmento === "Hotelaria";
    }
    if (subcategoryId === "aereo") {
      return company.segmento === "Transporte Aéreo";
    }
    if (subcategoryId === "terrestre") {
      return company.segmento === "Transporte Terrestre";
    }
    return true;
  });

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = 
      company.razaoSocial.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.nomeFantasia.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.cnpj.includes(searchTerm);
    
    const matchesSegment = selectedSegment === "all" || company.segmento === selectedSegment;
    
    return matchesSearch && matchesSegment;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Ativo":
        return "bg-green-100 text-green-800";
      case "Inativo":
        return "bg-red-100 text-red-800";
      case "Pendente":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCategoryTitle = () => {
    if (subcategoryId === "aereo") return "Aéreo";
    if (subcategoryId === "terrestre") return "Terrestre";
    if (categoryId === "hotel" || !subcategoryId) return "Hotelaria";
    return "Empresas";
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <CardTitle className="text-2xl font-bold">{getCategoryTitle()}</CardTitle>
            </div>
            <Button 
              className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]"
              onClick={() => setIsModalOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Nova Empresa
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                className="pl-10"
                placeholder="Buscar empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={selectedSegment} onValueChange={setSelectedSegment}>
              <SelectTrigger className="w-full md:w-[250px]">
                <SelectValue placeholder="Todos os segmentos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os segmentos</SelectItem>
                <SelectItem value="Transporte Aéreo">Transporte Aéreo</SelectItem>
                <SelectItem value="Transporte Terrestre">Transporte Terrestre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Razão Social</TableHead>
                  <TableHead>CNPJ</TableHead>
                  <TableHead>Segmento</TableHead>
                  <TableHead>Beneficiados</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.length > 0 ? (
                  filteredCompanies.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{company.razaoSocial}</div>
                          <div className="text-sm text-muted-foreground">{company.nomeFantasia}</div>
                        </div>
                      </TableCell>
                      <TableCell>{company.cnpj}</TableCell>
                      <TableCell>{company.segmento}</TableCell>
                      <TableCell>{company.beneficiados.toLocaleString('pt-BR')}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(company.status)}`}>
                          {company.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/companies/${company.id}`)}
                            className="text-[#0e7cb4] border-[#0e7cb4] hover:bg-[#0e7cb4] hover:text-white"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-[#0e7cb4] border-[#0e7cb4] hover:bg-[#0e7cb4] hover:text-white"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Editar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Nenhuma empresa encontrada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Cadastro de Nova Empresa */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cadastrar Nova Integração - {getCategoryTitle()}</DialogTitle>
            <DialogDescription>
              Preencha os dados da empresa e gere as credenciais de API para integração.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Passo 1: Dados Cadastrais */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Dados Cadastrais</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="razaoSocial">Razão Social *</Label>
                  <Input
                    id="razaoSocial"
                    placeholder="Ex: Aviões S.A."
                    value={formData.razaoSocial}
                    onChange={(e) => setFormData({ ...formData, razaoSocial: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <Input
                    id="cnpj"
                    placeholder="00.000.000/0000-00"
                    value={formData.cnpj}
                    onChange={handleCNPJChange}
                    maxLength={18}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nomeFantasia">Nome Fantasia *</Label>
                  <Input
                    id="nomeFantasia"
                    placeholder="Ex: Aviões"
                    value={formData.nomeFantasia}
                    onChange={(e) => setFormData({ ...formData, nomeFantasia: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="responsavelTecnico">Responsável Técnico *</Label>
                  <Input
                    id="responsavelTecnico"
                    placeholder="Nome do responsável pela integração"
                    value={formData.responsavelTecnico}
                    onChange={(e) => setFormData({ ...formData, responsavelTecnico: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="emailTecnico">E-mail Técnico *</Label>
                  <Input
                    id="emailTecnico"
                    type="email"
                    placeholder="tecnico@empresa.com.br"
                    value={formData.emailTecnico}
                    onChange={(e) => setFormData({ ...formData, emailTecnico: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Passo 2: Credenciais de API */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Credenciais de API</h3>
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="pt-6 space-y-4">
                  {/* Client ID */}
                  <div className="space-y-2">
                    <Label>Client ID</Label>
                    <div className="flex gap-2">
                      <Input
                        value={apiCredentials.clientId}
                        readOnly
                        className="bg-white font-mono text-sm"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(apiCredentials.clientId, "Client ID")}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Client Secret */}
                  <div className="space-y-2">
                    <Label>Client Secret</Label>
                    <div className="flex gap-2">
                      <Input
                        type={showSecret ? "text" : "password"}
                        value={showSecret ? apiCredentials.clientSecret : "••••••••••••••••"}
                        readOnly
                        className="bg-white font-mono text-sm"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowSecret(!showSecret)}
                      >
                        {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(apiCredentials.clientSecret, "Client Secret")}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Guarde este valor com segurança. Ele não será exibido novamente.
                    </p>
                  </div>

                  {/* Webhook URL */}
                  <div className="space-y-2">
                    <Label htmlFor="webhookUrl">Webhook URL (Opcional)</Label>
                    <Input
                      id="webhookUrl"
                      type="url"
                      placeholder="https://api.empresa.com.br/webhook/banclima"
                      value={formData.webhookUrl}
                      onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      URL onde a empresa deseja receber notificações de eventos.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Exemplo de Payload da API */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700">Exemplo de Payload da API</h3>
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="pt-4">
                  <pre className="text-xs overflow-x-auto text-gray-700">
{`{
  "transaction_id": "TICKET-998877",
  "issue_date": "2025-12-20T14:30:00Z",
  "customer": {
    "cpf": "123.456.789-00",
    "email": "joao.silva@email.com"
  },
  "financial": {
    "currency": "BRL",
    "total_amount": 1500.00,
    "fare_class": "executive"
  },
  "trip": {
    "origin_iata": "SDU",
    "destination_iata": "GRU",
    "flight_number": "LA3456",
    "aircraft_model": "A320",
    "distance_km": 366,
    "stops": 0,
    "saf_data": {
      "used_saf": true,
      "blend_percentage": 25.0
    }
  }
}`}
                  </pre>
                  <p className="text-xs text-muted-foreground mt-2">
                    Este é o formato JSON esperado para envio de transações via API.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]"
              onClick={handleSave}
            >
              Salvar e Gerar Manual de Integração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryItems;
